import React, { useState } from "react";
import { Table, Input, Button, FormGroup, Label } from "reactstrap";

/**
 * Notes tab — list existing notes (newest first) + a new-note text area + Add button.
 * Mirrors the legacy dgvNote behavior (Pass 1 §1.8).
 * Existing notes are never edited from this UI (legacy ProjectNote.Update silently no-ops
 * for non-submitter callers; we deliberately don't expose an edit affordance).
 */
export default function NotesTab({ notes, onAddNote, isAdding, readOnly, isArchived }) {
    const [text, setText] = useState("");

    const handleAdd = async () => {
        const t = (text || "").trim();
        if (!t) return;
        await onAddNote(t);
        setText("");
    };

    return (
        <div>
            {/* Go-live parity (2026-09-24): C5 — the New Note box is hidden (not just disabled)
                for people who may not add notes; the server enforces the same rule. */}
            {readOnly ? (
                <p className="small text-muted">
                    {isArchived
                        ? "This CR is archived — notes can no longer be added."
                        : "Only the author, the people assigned to this CR, their supervisors or a Contract Review admin can add notes."}
                </p>
            ) : (
                <FormGroup>
                    <Label className="fw-semibold">New Note</Label>
                    <Input
                        type="textarea"
                        rows={3}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Type a project note and click Add."
                    />
                    <div className="text-end mt-2">
                        <Button color="primary" size="sm" onClick={handleAdd} disabled={isAdding || !text.trim()}>
                            {isAdding ? "Adding…" : "Add Note"}
                        </Button>
                    </div>
                </FormGroup>
            )}

            <Table size="sm" bordered responsive className="align-middle">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: "20%" }}>Submitted By</th>
                        <th style={{ width: "16%" }}>Date</th>
                        <th>Note</th>
                    </tr>
                </thead>
                <tbody>
                    {(notes || []).map((n) => (
                        <tr key={n.id}>
                            <td>{n.submitByName || n.submittedBy}</td>
                            <td>{formatDateTime(n.datestamp)}</td>
                            <td style={{ whiteSpace: "pre-wrap" }}>{n.note}</td>
                        </tr>
                    ))}
                    {(notes || []).length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-center text-muted py-3">No notes yet.</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
}

function formatDateTime(s) {
    if (!s) return "";
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        return d.toLocaleString();
    } catch { return s; }
}
